import { Role } from 'auth';
import classNames from 'classnames';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import { IssuerApiKeyModal } from '@/modules/issuer-dashboard/components/nav-managment/IssuerApiKeyModal.tsx';
import { NavHistoryTab } from '@/modules/issuer-dashboard/components/nav-managment/NavHistoryTab.tsx';
import { UpdateLogTab } from '@/modules/issuer-dashboard/components/nav-managment/UpdateLogTab.tsx';
import { useGenerationTokenMutation } from '@/modules/issuer-dashboard/queries/nav.tsx';
import { Button, ModalProviderContext, PageLayout, TabItem, Tabs } from 'ui';

enum TabNames {
  NavHistory = 'NAV history',
  UpdateLog = 'Update log',
}

const TAB_NAME = 'tab-nav-management';

const IssuerNavManagement = () => {
  const { registerId } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const getRegisterTypeStrings = useRegisterTypeStrings();
  const [params] = useSearchParams();
  const paramTab = params.get(TAB_NAME);
  const isValidTab =
    paramTab &&
    Object.values(TabNames).includes(
      paramTab.replace('NAV', getRegisterTypeStrings('nav')) as TabNames,
    );
  const [activeTab, setActiveTab] = useState<TabNames>(
    `${
      isValidTab
        ? (paramTab.replace('NAV', getRegisterTypeStrings('nav')) as TabNames)
        : TabNames.NavHistory
    }`.replace('NAV', getRegisterTypeStrings('nav')) as TabNames,
  );

  useEffect(() => {
    setActiveTab(
      `${
        isValidTab
          ? (paramTab.replace('NAV', getRegisterTypeStrings('nav')) as TabNames)
          : TabNames.NavHistory
      }`.replace('NAV', getRegisterTypeStrings('nav')) as TabNames,
    );
  }, [registerId]);

  const tabItems = useMemo<TabItem<TabNames>[]>(() => {
    return [
      {
        name: TabNames.NavHistory.replace('NAV', getRegisterTypeStrings('nav')) as TabNames,
        container: <NavHistoryTab />,
      },
      {
        name: TabNames.UpdateLog,
        container: <UpdateLogTab />,
      },
    ];
  }, [getRegisterTypeStrings]);

  return (
    <PageLayout
      title={`${getRegisterTypeStrings('nav')} history`}
      headerAction={<ButtonGenerateAPI />}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      tabNameParam={TAB_NAME}
      tabs={tabItems}
      contentClassName="px-0"
    >
      <Tabs items={tabItems} activeTab={activeTab} />
    </PageLayout>
  );
};

interface ButtonGenerateAPIProps {
  className?: string;
  variant?: 'primary' | 'secondary' | 'plain';
}

export const ButtonGenerateAPI = ({ className, variant }: ButtonGenerateAPIProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const { registerId } = useOutletContext<IssuerDashboardWrapperOutletContext>();

  const { mutateAsync: mutateAsyncGenerationToken } = useGenerationTokenMutation();

  const handleGenerateAPIKey = async () => {
    const resp = await mutateAsyncGenerationToken();

    if (resp.access_token) {
      openModal(IssuerApiKeyModal, {
        apiKey: resp.access_token,
        registerId: !isNaN(+registerId) ? +registerId : 1,
      });
    }
  };

  return (
    <Button
      className={classNames('w-[150px]', className)}
      variant={variant}
      onClick={handleGenerateAPIKey}
    >
      Generate API Key
    </Button>
  );
};

export default withPrivateAccess(IssuerNavManagement, [Role.SR_ISSUER, Role.SR_ISSUER_ADMIN]);
